import { type CSSObject } from '@emotion/react';
import { useTheme } from '../../../../theme';
import { useIsTouchDevice } from '../../../../hooks/useIsTouchDevice';
import { useViewport } from '../../../../hooks/useViewport';
import { alpha } from '@mui/material/styles';

type Return = Record<'wrapper' | 'textarea' | 'error' | 'resizer', CSSObject>;

export const textareaClasses = {
  wrapper: 'textarea-wrapper',
  focused: 'textarea-focused',
  disabled: 'textarea-disabled',
};

export const useTextareaStyles = ({
  noResize = false,
  hasError = false,
  disabled = false,
  contentEditable = true,
}): Return => {
  const theme = useTheme();
  const isTouchDevice = useIsTouchDevice();
  const { isSmallViewport } = useViewport();

  return {
    wrapper: {
      [`&.${textareaClasses.wrapper}`]: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: theme.shape.radius8,
        outline: '0px solid transparent',
        border: `1px solid transparent`,
        boxShadow: `${theme.typography.inputShadow}, 0 0 0 1px ${
          hasError ? theme.input.border.error : theme.input.border.default
        }`,

        '&:hover': {
          borderColor: 'transparent',
          boxShadow: `${theme.typography.inputShadow}, 0 0 0 ${
            hasError ? 2 : 1
          }px ${
            hasError
              ? theme.input.border.errorActive
              : theme.input.border.active
          }`,
          outlineWidth: hasError ? 1 : 0,
          outlineColor: theme.input.border.errorActive,
          '&:not:has(:disabled))~svg': {
            color: hasError ? theme.icon.secondary : theme.icon.brandlight,
          },
        },

        [`&.${textareaClasses.disabled}`]: {
          borderColor: 'transparent',
          backgroundColor: theme.input.background.disabled,
          boxShadow: `${theme.typography.inputShadow}, 0 0 0 1px ${theme.input.border.default}`,
        },
      },
      [`&.${textareaClasses.focused}:not(.${textareaClasses.disabled})`]: {
        borderColor: 'transparent',
        boxShadow: `${theme.typography.inputShadow}, 0 0 0 2px ${
          hasError ? theme.input.border.errorActive : theme.input.border.active
        }`,
        outlineWidth: theme.spacing(1),
        outlineColor: hasError
          ? theme.input.border.errorFocused
          : theme.input.border.focused,
        '&~svg': {
          color: hasError ? theme.icon.secondary : theme.icon.brand,
        },
      },
    },
    textarea: {
      display: 'block',
      position: 'relative',
      minWidth: '100%',
      minHeight: '80px',
      zIndex: 1,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      caretColor: hasError
        ? theme.palette.alert.negative[100]
        : theme.blinkingCursor.cursor,

      backgroundColor: theme.input.background.default,
      fontFamily: 'inherit',
      resize: Boolean(noResize) ? 'none' : 'vertical',
      border: 'none',
      outline: 'none',
      color:
        disabled || !contentEditable
          ? contentEditable
            ? alpha(theme.text.interactive.disabled, 0.3)
            : theme.text.interactive.disabled
          : theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightRegular,

      ...(isTouchDevice && isSmallViewport
        ? theme.typography.bodyM
        : theme.typography.bodyS),

      '::placeholder': {
        color:
          disabled || !contentEditable
            ? contentEditable
              ? alpha(theme.text.interactive.disabled, 0.3)
              : theme.text.interactive.disabled
            : theme.text.interactive.disabled,
      },

      ':disabled': {
        backgroundColor: theme.input.background.disabled,
      },

      '::-webkit-resizer': {
        display: 'none',
      },
    },
    error: {
      position: 'absolute',
      right: '2px',
      top: '2px',
      zIndex: 2,
    },
    resizer: noResize
      ? { display: 'none' }
      : {
          position: 'absolute',
          right: '-10px',
          bottom: '-10px',
          color: theme.icon.secondary,
          cursor: 'se-resize',
          pointerEvents: 'none',
          zIndex: 2,
          '&:hover': {
            color: theme.icon.brandlight,
          },

          // Firefox doesn't support custom resize handles
          '@supports (-moz-appearance: none)': {
            display: 'none',
          },
        },
  };
};
